import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Settings, MapPin, UserCog, Users, Shield, Plus, Pencil, Trash2,
  Save, X, Phone, Mail, Wrench, Building2, ChevronRight
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";

const TABS = [
  { id: "settings", label: "Shop Settings", icon: Settings },
  { id: "locations", label: "Locations", icon: MapPin },
  { id: "technicians", label: "Technicians", icon: UserCog },
  { id: "users", label: "Users & Roles", icon: Users },
];

const ROLES = ["Super Admin", "Admin", "Supervisor", "Technician", "Receptionist"];
const SPECIALIZATIONS = ["Battery", "Motor", "Charger", "Diagnostics", "General", "Brakes", "Electrical"];

const SuperAdminPage = () => {
  const [activeTab, setActiveTab] = useState("settings");

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-[22px] font-bold text-[#1E293B]" data-testid="admin-title">Super Admin</h1>
        <p className="text-[13px] text-[#94A3B8]">Manage workshop settings, locations, technicians, and users</p>
      </div>

      <div className="flex gap-6">
        {/* Left sidebar tabs */}
        <div className="w-[220px] shrink-0 space-y-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} data-testid={`admin-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-left text-[13px] font-medium transition-all ${
                  isActive ? "bg-[#3B82F6] text-white shadow-sm" : "text-[#64748B] hover:bg-[#F1F5F9]"
                }`}>
                <Icon className="w-4 h-4" />
                {tab.label}
                {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeTab === "settings" && <ShopSettingsPanel />}
          {activeTab === "locations" && <LocationsPanel />}
          {activeTab === "technicians" && <TechniciansPanel />}
          {activeTab === "users" && <UsersPanel />}
        </div>
      </div>
    </div>
  );
};

// ==================== SHOP SETTINGS ====================
const ShopSettingsPanel = () => {
  const [settings, setSettings] = useState({
    shop_name: "", shop_phone: "", shop_email: "", shop_address: "",
    gst_number: "", operating_hours: "9:00 AM - 6:00 PM", currency: "INR",
    booking_slot_duration: "30", max_bookings_per_slot: "3"
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`).then(r => {
      setSettings(prev => ({ ...prev, ...r.data }));
    }).catch(err => { console.error("Failed to load settings:", err); });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const set = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#F1F5F9]">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#3B82F6]" />
          <h2 className="text-[16px] font-bold text-[#1E293B]">Workshop Settings</h2>
        </div>
        <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving}
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] gap-1.5 h-9">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SettingsField label="Workshop Name" value={settings.shop_name} onChange={v => set("shop_name", v)} testId="setting-shop-name" />
        <SettingsField label="Phone Number" value={settings.shop_phone} onChange={v => set("shop_phone", v)} testId="setting-shop-phone" />
        <SettingsField label="Email" value={settings.shop_email} onChange={v => set("shop_email", v)} testId="setting-shop-email" />
        <SettingsField label="GST Number" value={settings.gst_number} onChange={v => set("gst_number", v)} testId="setting-gst" />
        <div className="md:col-span-2">
          <SettingsField label="Address" value={settings.shop_address} onChange={v => set("shop_address", v)} testId="setting-address" />
        </div>
      </div>

      <div className="border-t border-[#F1F5F9] mt-5 pt-5">
        <h3 className="text-[13px] font-semibold text-[#475569] mb-3">Booking Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SettingsField label="Operating Hours" value={settings.operating_hours} onChange={v => set("operating_hours", v)} testId="setting-hours" />
          <SettingsField label="Slot Duration (minutes)" value={settings.booking_slot_duration} onChange={v => set("booking_slot_duration", v)} testId="setting-slot-duration" />
          <SettingsField label="Max Bookings Per Slot" value={settings.max_bookings_per_slot} onChange={v => set("max_bookings_per_slot", v)} testId="setting-max-bookings" />
        </div>
      </div>
    </div>
  );
};

const SettingsField = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-[11px] text-[#94A3B8] mb-1.5 block">{label}</Label>
    <Input data-testid={testId} value={value || ""} onChange={e => onChange(e.target.value)}
      className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
  </div>
);

// ==================== LOCATIONS ====================
const LocationsPanel = () => {
  const [locations, setLocations] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", phone: "" });

  const fetchLocations = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/locations`); setLocations(r.data); }
    catch (err) { toast.error("Failed to fetch locations"); }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editItem) {
        await axios.put(`${API}/admin/locations/${editItem.id}`, form);
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/admin/locations`, form);
        toast.success("Location created");
      }
      setFormOpen(false); setEditItem(null); setForm({ name: "", address: "", phone: "" });
      fetchLocations();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save location"); }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Delete location "${loc.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/locations/${loc.id}`);
      toast.success("Location deleted");
      fetchLocations();
    } catch { toast.error("Failed to delete location"); }
  };

  const openEdit = (loc) => {
    setEditItem(loc);
    setForm({ name: loc.name, address: loc.address, phone: loc.phone });
    setFormOpen(true);
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#F1F5F9]">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#3B82F6]" />
          <h2 className="text-[16px] font-bold text-[#1E293B]">Locations</h2>
          <span className="text-[11px] bg-[#F1F5F9] text-[#64748B] px-2 py-0.5 rounded-full font-medium">{locations.length}</span>
        </div>
        <Button data-testid="add-location-btn" onClick={() => { setEditItem(null); setForm({ name: "", address: "", phone: "" }); setFormOpen(true); }}
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] gap-1.5 h-9">
          <Plus className="w-3.5 h-3.5" /> Add Location
        </Button>
      </div>

      {locations.length === 0 ? (
        <p className="text-center py-8 text-[13px] text-[#94A3B8]">No locations configured. Add your first location.</p>
      ) : (
        <div className="space-y-2">
          {locations.map(loc => (
            <div key={loc.id} data-testid={`location-row-${loc.id}`}
              className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#F1F5F9] hover:bg-[#FAFBFC] transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#3B82F6]" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#1E293B]">{loc.name}</p>
                  <p className="text-[11px] text-[#94A3B8]">{loc.address || "No address"}{loc.phone ? ` | ${loc.phone}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${loc.is_active !== false ? "bg-[#F0FDF4] text-[#16A34A]" : "bg-[#FEF2F2] text-[#DC2626]"}`}>
                  {loc.is_active !== false ? "Active" : "Inactive"}
                </span>
                <Button size="sm" variant="ghost" onClick={() => openEdit(loc)} className="h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5 text-[#64748B]" /></Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(loc)} className="h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5 text-[#DC2626]" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-sm" data-testid="location-form-modal">
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Location" : "Add Location"}</DialogTitle>
            <DialogDescription>Configure workshop location details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Name <span className="text-red-500">*</span></Label>
              <Input data-testid="location-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Main Workshop" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
            </div>
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Address</Label>
              <Input data-testid="location-address-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                placeholder="Full address" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
            </div>
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Phone</Label>
              <Input data-testid="location-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="Contact number" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="location-save-btn" onClick={handleSave} className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px]">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {editItem ? "Update" : "Create"}
              </Button>
              <Button variant="outline" onClick={() => setFormOpen(false)} className="text-[12px]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== TECHNICIANS ====================
const TechniciansPanel = () => {
  const [technicians, setTechnicians] = useState([]);
  const [locations, setLocations] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", specialization: "", location_id: "" });

  const fetchTechnicians = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/technicians`); setTechnicians(r.data); }
    catch (err) { toast.error("Failed to fetch technicians"); }
  }, []);

  const fetchLocations = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/locations`); setLocations(r.data); }
    catch (err) { console.error("Failed to fetch locations:", err); }
  }, []);

  useEffect(() => { fetchTechnicians(); fetchLocations(); }, [fetchTechnicians, fetchLocations]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      const payload = { ...form, location_id: form.location_id === "none" ? null : form.location_id };
      if (editItem) {
        await axios.put(`${API}/admin/technicians/${editItem.id}`, payload);
        toast.success("Technician updated");
      } else {
        await axios.post(`${API}/admin/technicians`, payload);
        toast.success("Technician added");
      }
      setFormOpen(false); setEditItem(null); setForm({ name: "", phone: "", email: "", specialization: "", location_id: "" });
      fetchTechnicians();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save technician"); }
  };

  const handleDelete = async (tech) => {
    if (!window.confirm(`Remove technician "${tech.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/technicians/${tech.id}`);
      toast.success("Technician removed");
      fetchTechnicians();
    } catch { toast.error("Failed to remove technician"); }
  };

  const openEdit = (tech) => {
    setEditItem(tech);
    setForm({ name: tech.name, phone: tech.phone, email: tech.email, specialization: tech.specialization, location_id: tech.location_id || "none" });
    setFormOpen(true);
  };

  const getLocationName = (id) => locations.find(l => l.id === id)?.name || "-";

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#F1F5F9]">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <UserCog className="w-5 h-5 text-[#3B82F6]" />
          <h2 className="text-[16px] font-bold text-[#1E293B]">Technicians</h2>
          <span className="text-[11px] bg-[#F1F5F9] text-[#64748B] px-2 py-0.5 rounded-full font-medium">{technicians.length}</span>
        </div>
        <Button data-testid="add-technician-btn" onClick={() => { setEditItem(null); setForm({ name: "", phone: "", email: "", specialization: "", location_id: "" }); setFormOpen(true); }}
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] gap-1.5 h-9">
          <Plus className="w-3.5 h-3.5" /> Add Technician
        </Button>
      </div>

      {technicians.length === 0 ? (
        <p className="text-center py-8 text-[13px] text-[#94A3B8]">No technicians added yet.</p>
      ) : (
        <div className="space-y-2">
          {technicians.map(tech => (
            <div key={tech.id} data-testid={`technician-row-${tech.id}`}
              className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#F1F5F9] hover:bg-[#FAFBFC] transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#EDE9FE] flex items-center justify-center">
                  <UserCog className="w-4 h-4 text-[#7C3AED]" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#1E293B]">{tech.name}</p>
                  <div className="flex items-center gap-2 text-[11px] text-[#94A3B8]">
                    {tech.specialization && <span className="flex items-center gap-0.5"><Wrench className="w-3 h-3" /> {tech.specialization}</span>}
                    {tech.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" /> {tech.phone}</span>}
                    {tech.location_id && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {getLocationName(tech.location_id)}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${tech.is_active !== false ? "bg-[#F0FDF4] text-[#16A34A]" : "bg-[#FEF2F2] text-[#DC2626]"}`}>
                  {tech.is_active !== false ? "Active" : "Inactive"}
                </span>
                <Button size="sm" variant="ghost" onClick={() => openEdit(tech)} className="h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5 text-[#64748B]" /></Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(tech)} className="h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5 text-[#DC2626]" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-sm" data-testid="technician-form-modal">
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Technician" : "Add Technician"}</DialogTitle>
            <DialogDescription>Manage technician details and assignment</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Name <span className="text-red-500">*</span></Label>
              <Input data-testid="tech-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Technician name" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-[#94A3B8] mb-1 block">Phone</Label>
                <Input data-testid="tech-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
              </div>
              <div>
                <Label className="text-[11px] text-[#94A3B8] mb-1 block">Email</Label>
                <Input data-testid="tech-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="Email" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Specialization</Label>
              <Select value={form.specialization} onValueChange={v => setForm({ ...form, specialization: v })}>
                <SelectTrigger data-testid="tech-spec-select" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]">
                  <SelectValue placeholder="Select specialization" />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALIZATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Assigned Location</Label>
              <Select value={form.location_id} onValueChange={v => setForm({ ...form, location_id: v })}>
                <SelectTrigger data-testid="tech-location-select" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="tech-save-btn" onClick={handleSave} className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px]">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {editItem ? "Update" : "Add"}
              </Button>
              <Button variant="outline" onClick={() => setFormOpen(false)} className="text-[12px]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== USERS & ROLES ====================
const UsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "Technician" });

  const fetchUsers = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/users`); setUsers(r.data); }
    catch (err) { toast.error("Failed to fetch users"); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    if (!editItem && !form.password.trim()) { toast.error("Password is required for new users"); return; }
    try {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.password.trim()) payload.password = form.password;
      if (editItem) {
        await axios.put(`${API}/admin/users/${editItem.id}`, payload);
        toast.success("User updated");
      } else {
        await axios.post(`${API}/admin/users`, payload);
        toast.success("User created");
      }
      setFormOpen(false); setEditItem(null); setForm({ name: "", email: "", password: "", role: "Technician" });
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save user"); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${user.id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch { toast.error("Failed to delete user"); }
  };

  const openEdit = (user) => {
    setEditItem(user);
    setForm({ name: user.name, email: user.email, password: "", role: user.role || "Technician" });
    setFormOpen(true);
  };

  const ROLE_COLORS = {
    "Super Admin": { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]" },
    "Admin": { bg: "bg-[#FFF7ED]", text: "text-[#EA580C]" },
    "Supervisor": { bg: "bg-[#EFF6FF]", text: "text-[#2563EB]" },
    "Technician": { bg: "bg-[#EDE9FE]", text: "text-[#7C3AED]" },
    "Receptionist": { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]" },
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#F1F5F9]">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#3B82F6]" />
          <h2 className="text-[16px] font-bold text-[#1E293B]">Users & Roles</h2>
          <span className="text-[11px] bg-[#F1F5F9] text-[#64748B] px-2 py-0.5 rounded-full font-medium">{users.length}</span>
        </div>
        <Button data-testid="add-user-btn" onClick={() => { setEditItem(null); setForm({ name: "", email: "", password: "", role: "Technician" }); setFormOpen(true); }}
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px] gap-1.5 h-9">
          <Plus className="w-3.5 h-3.5" /> Add User
        </Button>
      </div>

      {users.length === 0 ? (
        <p className="text-center py-8 text-[13px] text-[#94A3B8]">No users found.</p>
      ) : (
        <div className="space-y-2">
          {users.map(user => {
            const rc = ROLE_COLORS[user.role] || ROLE_COLORS["Technician"];
            return (
              <div key={user.id || user._id} data-testid={`user-row-${user.id || user._id}`}
                className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#F1F5F9] hover:bg-[#FAFBFC] transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FEF2F2] flex items-center justify-center">
                    <Users className="w-4 h-4 text-[#DC2626]" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-[#1E293B]">{user.name}</p>
                    <div className="flex items-center gap-2 text-[11px] text-[#94A3B8]">
                      <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" /> {user.email}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${rc.bg} ${rc.text}`}>{user.role || "Technician"}</span>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(user)} className="h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5 text-[#64748B]" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(user)} className="h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5 text-[#DC2626]" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-sm" data-testid="user-form-modal">
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>Manage user accounts and role assignments</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Name <span className="text-red-500">*</span></Label>
              <Input data-testid="user-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Full name" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
            </div>
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Email <span className="text-red-500">*</span></Label>
              <Input data-testid="user-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="user@example.com" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
            </div>
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Password {editItem ? "(leave blank to keep)" : ""} <span className="text-red-500">{!editItem && "*"}</span></Label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder={editItem ? "Leave blank to keep current" : "Set password"} className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]" />
            </div>
            <div>
              <Label className="text-[11px] text-[#94A3B8] mb-1 block">Role</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="h-10 bg-[#F8FAFC] border-[#E2E8F0] text-[13px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="user-save-btn" onClick={handleSave} className="flex-1 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12px]">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {editItem ? "Update" : "Create User"}
              </Button>
              <Button variant="outline" onClick={() => setFormOpen(false)} className="text-[12px]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
